import React, { useEffect, useRef } from "react";

import flatten from "lodash/flatten";
import { UploadResult } from "@uppy/core";

import Uppy, { UppyFile } from "@uppy/core";
import { DashboardModal } from "@uppy/react";
import Webcam from "@uppy/webcam";
import Url from "@uppy/url";
import AwsS3Multipart from "@uppy/aws-s3-multipart";
import ImageCompressor from "./ImageCompressor";

import "@uppy/core/dist/style.min.css";
import "@uppy/dashboard/dist/style.min.css";
import "@uppy/webcam/dist/style.min.css";

import "./FileUpload.less";

import { UppyFileMetaData } from "@/Contracts";
import { validateYoutubeLink } from "@/Contracts/Utils";
import ZMessage from "@molecules/ZMessage";

export enum UppyAllowedFileTypes {
	All = "*/*",
	Compressed = "application/zip,application/gzip,application/x-bzip,application/x-bzip2,application/x-7z-compressed,multipart/x-zip,application/x-zip-compressed,application/rar,application/vnd.rar,application/x-rar-compressed,application/x-tar,application/octet-stream",
	Image = "image/jpg,image/jpeg,image/png,image/gif,image/svg,image/webp,image/svg+xml",
	Video = "video/mp4,.mkv,video/webm,video/x-matroska",
	Favicon = "image/vnd.microsoft.icon,image/x-icon"
}

export const NotAllowedExtensions = /\.(exe|js|htaccess)$/gi;

export interface Props {
	readonly visible: boolean;
	readonly maxFileSize: number;
	readonly maxNumberOfFiles: number;
	readonly minNumberOfFiles: number;
	readonly allowedFileTypes: UppyAllowedFileTypes[];
	readonly allowMultipleUploads: boolean;
	readonly onClose: () => void;
	readonly onComplete: (result: Uppy.UploadResult<UppyFileMetaData>) => void;
}

const FileUpload: React.FC<Props> = props => {
	let invalidExtensionError: boolean;
	const uppy = useRef<Uppy.Uppy>(
		Uppy({
			id: "MyUppy",
			autoProceed: true,
			restrictions: {
				maxFileSize: props?.maxFileSize ? props.maxFileSize : 26214400,
				maxNumberOfFiles: props?.maxNumberOfFiles
					? props.maxNumberOfFiles
					: 1,
				minNumberOfFiles: props?.minNumberOfFiles
					? props.minNumberOfFiles
					: 1,
				allowedFileTypes:
					props?.allowedFileTypes?.length > 0
						? flatten(
								props?.allowedFileTypes.map(fileType =>
									fileType.split(",")
								)
						  )
						: [UppyAllowedFileTypes.All]
			},
			allowMultipleUploads: props?.allowMultipleUploads
				? props.allowMultipleUploads
				: false,

			onBeforeFileAdded: (
				currentFile: UppyFile,
				_files: { [key: string]: UppyFile }
			) => {
				const shouldNotUpload = NotAllowedExtensions.test(
					currentFile?.name
				);
				if (shouldNotUpload) {
					invalidExtensionError = true;
					return false;
				}
				return true;
			},
			onBeforeUpload: (files: { [key: string]: UppyFile }) => {
				Object.keys(files).forEach(fileId => {
					if (fileId && files[fileId] && files[fileId].data) {
						if (files[fileId].source !== "MyUrl") {
							const file = files[fileId];
							const data = file.data;
							const url = URL.createObjectURL(data);
							if (
								props.allowedFileTypes.indexOf(
									UppyAllowedFileTypes.Image
								) > -1
							) {
								const image = new Image();
								image.src = url;
								image.onload = () => {
									uppy.current.setFileMeta(fileId, {
										width: image.width,
										height: image.height
									});
									URL.revokeObjectURL(url);
								};
							} else if (
								props.allowedFileTypes.indexOf(
									UppyAllowedFileTypes.Video
								) > -1
							) {
								uppy.current.setFileMeta(fileId, {
									thumbnail: ""
								});
							}
						} else {
							if (
								props.allowedFileTypes.indexOf(
									UppyAllowedFileTypes.Image
								) > -1
							) {
								uppy.current.setFileMeta(fileId, {
									width: 600,
									height: 300
								});
							} else if (
								props.allowedFileTypes.indexOf(
									UppyAllowedFileTypes.Video
								) > -1
							) {
								uppy.current.setFileMeta(fileId, {
									thumbnail: ""
								});
							}
						}
					}
				});
				return files;
			}
		})
			.use(ImageCompressor)
			.use(Webcam, {
				id: "MyWebcam",
				companionUrl: process.env.REACT_APP_FILES_HOST
			})
			.use(Url, {
				id: "MyUrl",
				companionUrl: process.env.REACT_APP_FILES_HOST
			})
			.use(AwsS3Multipart, {
				id: "AwsS3Multipart",
				companionUrl: process.env.REACT_APP_FILES_HOST,
				abortMultipartUpload: function (
					this: AwsS3Multipart.AwsS3MultipartOptions,
					file,
					{ key, uploadId }
				) {
					if (key && uploadId)
						return this?.abortMultipartUpload?.(file, {
							key,
							uploadId
						});
				}
			})
			.on("complete", (result: Uppy.UploadResult<UppyFileMetaData>) => {
				props.onComplete(result);
			})
			.on("error", (error: any) => {
				console.error(error.toString());
				ZMessage.error(error.toString());
			})
			.on(
				"upload-error",
				(_file: UppyFile, error: any, _response: any) => {
					console.error(error.toString());
					ZMessage.error(error.toString());
				}
			)
	);

	useEffect(() => {
		if (props && uppy) {
			uppy.current.on(
				"restriction-failed",
				(file: UppyFile, error: any) => {
					if (
						(file.type?.includes("text/plain") ||
							file.type?.includes("text/html")) &&
						file.remote &&
						file.remote.body
					) {
						for (const url of Object.values(file.remote.body)) {
							if (validateYoutubeLink(url)) {
								const result: UploadResult<UppyFileMetaData> = {
									successful: [{ uploadURL: url, ...file }],
									failed: []
								};
								props.onComplete(result);
								onClose();
								return;
							}
							ZMessage.error("Invalid Link");
						}
					} else if (invalidExtensionError) {
						ZMessage.error(`${file.name} has an invalid extension`);
					} else {
						ZMessage.error(error.toString());
					}
				}
			);
		}

		return () => {
			uppy.current.close();
		};
	}, [props]);

	const onClose = () => {
		uppy.current.reset();
		props?.onClose();
	};

	if (!props?.visible) {
		return null;
	} else
		return (
			<DashboardModal
				uppy={uppy.current}
				plugins={["MyWebcam", "MyUrl"]}
				open={props.visible}
				onRequestClose={onClose}
				width={300}
				height={300}
				proudlyDisplayPoweredByUppy={false}
				closeAfterFinish={true}
				hideProgressAfterFinish={true}
				disablePageScrollWhenModalOpen={true}
				disableInformer={true}
				disableStatusBar={false}
				disableThumbnailGenerator={false}
				closeModalOnClickOutside={true}
				showProgressDetails={true}
				showLinkToFileUploadResult={false}
				showSelectedFiles={true}
				hideUploadButton={false}
				hideRetryButton={false}
				hidePauseResumeButton={true}
				hideCancelButton={false}
			/>
		);
};

export default FileUpload;
